/**
 * <s-gallery-slider>
 * Compact product gallery: a scroll-snap track of slides plus thumbnail buttons.
 * Thumbnails invoke `command="--slide"` with `value` set to the slide index;
 * the element scrolls the track and keeps `aria-current` in sync with the
 * visible slide.
 */
class SGallerySlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.onCommand = (event) => {
      if (event.command !== '--slide') return;
      const index = Number(event.source?.value ?? 0);
      this.goTo(index);
    };
    this.onScroll = () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.syncFromScroll(), 80);
    };

    this.addEventListener('command', this.onCommand);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.markCurrent(0);
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.onCommand);
    this.track?.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.scrollTimer);
  }

  goTo(index) {
    const slide = this.track.children[index];
    if (!slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.markCurrent(index);
  }

  syncFromScroll() {
    const width = this.track.clientWidth || 1;
    this.markCurrent(Math.round(this.track.scrollLeft / width));
  }

  markCurrent(index) {
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const isCurrent = Number(thumb.value) === index;
      thumb.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    });
  }
}

if (!customElements.get('s-gallery-slider')) {
  customElements.define('s-gallery-slider', SGallerySlider);
}
